import { CSSProperties, useState } from 'react'
import clsx from 'clsx'
import styles from './meshAvatar.module.css'
import hexOutlinedSvg from '../../../assets/hex-outlined.svg'

type MeshAvatarProps = {
  name?: string
  imageUrl?: string
  size?: number
  borderColor?: string
  textColor?: string
  backgroundColor?: string
  className?: string
  style?: CSSProperties
}

const getInitials = (name?: string): string | null => {
  if (!name?.trim()) return null
  const words = name.trim().split(/\s+/).filter(Boolean)
  const initials = words
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()
  return initials || null
}

export const MeshAvatar = ({
  name,
  imageUrl,
  size = 36,
  borderColor,
  textColor,
  backgroundColor,
  className,
  style,
}: MeshAvatarProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const initials = getInitials(name)

  const containerStyle: CSSProperties = {
    width: size,
    height: size,
    ...style,
  }

  const placeholderStyle: CSSProperties = {
    width: size,
    height: size,
    backgroundImage: `url(${hexOutlinedSvg})`,
    ...(backgroundColor && { backgroundColor }),
  }

  const textStyle: CSSProperties = {
    fontSize: size * 0.35,
    ...(textColor && { color: textColor }),
  }

  // Image avatar with hex mask (shows placeholder while loading)
  if (imageUrl) {
    return (
      <div className={clsx(styles.container, className)} style={containerStyle}>
        <div
          className={styles.placeholder}
          style={{
            ...placeholderStyle,
            opacity: imageLoaded ? 0 : 1,
            ...(borderColor &&
              ({
                '--avatar-border-color': borderColor,
              } as CSSProperties)),
          }}
        >
          {initials ? (
            <span className={styles.initials} style={textStyle}>
              {initials}
            </span>
          ) : (
            <svg
              className={styles.defaultIcon}
              style={{ width: size * 0.4, height: size * 0.4 }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>
        <img
          src={imageUrl}
          alt={name || 'Avatar'}
          className={styles.hexMask}
          style={{
            width: size,
            height: size,
            opacity: imageLoaded ? 1 : 0,
          }}
          onLoad={() => setImageLoaded(true)}
        />
      </div>
    )
  }

  // Initials or placeholder with hex outline (no image)
  return (
    <div className={clsx(styles.container, className)} style={containerStyle}>
      <div
        className={styles.placeholder}
        style={{
          ...placeholderStyle,
          ...(borderColor &&
            ({
              '--avatar-border-color': borderColor,
            } as CSSProperties)),
        }}
      >
        {initials ? (
          <span className={styles.initials} style={textStyle}>
            {initials}
          </span>
        ) : (
          <svg
            className={styles.defaultIcon}
            style={{ width: size * 0.4, height: size * 0.4 }}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
        )}
      </div>
    </div>
  )
}

MeshAvatar.displayName = 'MeshAvatar'
